import { create } from 'zustand'

import { safeStorage } from '../storage.ts'

/**
 * 겉모습 «취향» 상태 — 강조색 · 유리 효과.
 *
 * `themeStore` 와 같은 꼴이다: 값은 상태만 들고 있고 «색»은 모른다. 색은 @ctcells/ui 의
 * 토큰 CSS 가 갖고, 여기는 `<html>` 에 속성 한 개를 박는 일만 한다.
 *
 * 규약(테마와 같다):
 *  - 판정은 **JS 한 곳**이다. CSS 에 매체 질의로 같은 판정을 두지 않는다.
 *  - 고른 값은 브라우저 저장소에 남는다. 저장이 막혀도 이번 세션에선 적용된다.
 *  - 첫 렌더 «전»에 박는다(`initAppearance` · mountApp). 뒤로 미루면 한 프레임 번쩍인다.
 *
 * 🔴 **통합 콘솔(`data-skin="console"`)에서만 뜻이 있다.** 강조 계단은 콘솔 스킨 팔레트
 *    아래에만 서 있고, 스킨이 없는 앱(장비 관점 · 모듈 3종)은 이 속성을 달지 않는다 —
 *    `mountApp` 이 스킨을 넘겨받은 앱에서만 `initAppearance` 를 부른다.
 */

/**
 * 고를 수 있는 강조색 — **유일한 출처다.** 개인 설정 화면이 이 배열을 그린다.
 *
 * eric 확정 2026-09-22(오후): 「지금 상태인 파랑과 검정 이렇게 두 개의 색상만 선택 가능하게」 — 남색은 걷었다.
 * eric 확정 2026-09-23 「민트색 추가해줘」로 민트가 셋째로 섰다가,
 * eric 2026-09-28 「테마색에서.. 초록색 부분은 빼줘」로 걷혔다 — 🔴 녹색 계열을 되살리지 마라.
 *   blue   파랑 — 기본(eric 확정 2026-09-22 「이걸 메인 테마색으로, 기존의 청록색은 없애고」)
 *   ink    검정 — 색 없는 방안 A(중성 계단을 강조로 쓴다)
 *
 * ⚠ 붉은·주황·노랑 계열을 여기 더하지 마라 — 위험·주의와 헷갈린다.
 * ⚠ 값을 늘리려면 palette.css 의 「강조 계단 고르기」에 블록을 같이 세워야 한다.
 */
export const ACCENTS = ['blue', 'ink'] as const

export type Accent = (typeof ACCENTS)[number]

export const ACCENT_STORAGE_KEY = 'ctcells:accent'

/**
 * 고른 적이 없을 때의 값. 저장소에 옛 값(`teal` · `mint`)이 남아 있어도 목록 밖이라 이 값으로 떨어진다 —
 * 🔴 속성이 목록 밖 값이면 강조 계단이 안 서서 화면이 색 없이 선다. 그래서 읽을 때 걸러 낸다.
 */
export const DEFAULT_ACCENT: Accent = 'blue'

/**
 * 🔴 **숨김 스위치 — 설정 화면의 「테마색」 줄을 그리는가**(eric 2026-10-29 「테마색 이거 테마색 두 개잖아. 이거 없애줘」 → 「일단 숨기기로 해줘」).
 * `false` 인 동안 ⓐ 개인 설정 화면이 줄을 안 그리고(`PreferenceSettings`) ⓑ 사람이 못 바꾸니 **저장값이 검정이어도 파랑**(`DEFAULT_ACCENT`)으로 읽는다.
 * 강조색 고르기 · 검정 계단(palette.css) · 사전 키 · 저장 키(`ctcells:accent`)는 지우지 않았다 — 되살릴 때는 이 한 줄을 `true` 로만 바꾼다
 * (시험도 이 값으로 갈린다 — `preferences.test.tsx` · `appearanceStore.test.ts`).
 */
export const ACCENT_PICKER_SHOWN: boolean = false

function isAccent(value: string | null): value is Accent {
  return (ACCENTS as readonly string[]).includes(value ?? '')
}

function readStoredAccent(): Accent {
  /* 줄이 숨은 동안은 저장값을 읽지 않는다 — 예전에 검정을 고른 사람도 파랑으로 선다(키는 남겨 둔다 · 되살리면 다시 읽힌다) */
  if (!ACCENT_PICKER_SHOWN) return DEFAULT_ACCENT
  const raw = safeStorage.read(ACCENT_STORAGE_KEY)
  return isAccent(raw) ? raw : DEFAULT_ACCENT
}

/** 적용 지점도 한 곳. 선택자는 `[data-accent]` 다(palette.css 와 짝) */
export function applyAccent(accent: Accent): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset['accent'] = accent
}

interface AccentState {
  accent: Accent
  // 화살표 타입으로 선언한다 — 셀렉터로 꺼내 쓸 때 unbound-method 경고를 피한다
  setAccent: (accent: Accent) => void
}

export const useAccentStore = create<AccentState>((set) => ({
  accent: readStoredAccent(),

  setAccent: (accent) => {
    safeStorage.write(ACCENT_STORAGE_KEY, accent)
    set({ accent })
    applyAccent(accent)
  },
}))

/*
 * ── 유리 효과 (eric 확정 2026-09-22) ───────────────────────────────────────────
 *
 * eric 원문: 「아이폰의 리퀴드 글라스 테마도 가능해?」 ·
 *            「3가지 테마색하고 리퀴드 글라스도 선택 해제 가능하게 해줘」
 */

/** 켬/끔 두 값. 「없음」을 뜻하는 빈 값을 만들지 않는다 — 속성이 늘 붙어 있어야 판정이 한 곳이다 */
export const GLASS_MODES = ['off', 'on'] as const

export type GlassMode = (typeof GLASS_MODES)[number]

export const GLASS_STORAGE_KEY = 'ctcells:glass'

/**
 * 🔴 **기본은 «끔»이다.** 실장비 셸(Tauri · WebKitGTK)에서 `backdrop-filter` 의 렌더·성능이
 *    미검증이다. 검증 전까지 켜는 것은 사용자의 선택으로 둔다.
 */
/** eric 2026-09-22(오후) 「리퀴드 글라스는 항상 enable 하고 설정에서 빼줘」 — 기본이 켬이고 설정 화면에 줄이 없다. 저장소·속성은 남겨 둔다 */
export const DEFAULT_GLASS: GlassMode = 'on'

function isGlass(value: string | null): value is GlassMode {
  return (GLASS_MODES as readonly string[]).includes(value ?? '')
}

/** 적용 지점 한 곳. 선택자는 `[data-glass='on']` 이다(theme.css 와 짝) */
export function applyGlass(mode: GlassMode): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset['glass'] = mode
}

interface GlassState {
  glass: GlassMode
  setGlass: (mode: GlassMode) => void
}

export const useGlassStore = create<GlassState>((set) => ({
  glass: (() => {
    const raw = safeStorage.read(GLASS_STORAGE_KEY)
    return isGlass(raw) ? raw : DEFAULT_GLASS
  })(),

  setGlass: (mode) => {
    safeStorage.write(GLASS_STORAGE_KEY, mode)
    set({ glass: mode })
    applyGlass(mode)
  },
}))

/**
 * 첫 렌더 전에 부른다(mountApp). 저장된 취향을 `<html>` 에 반영한다.
 *
 * 🔴 스킨이 있는 앱에서만 부른다 — 스킨 없는 앱에 속성을 달면 아무 계단도 없는
 *    `--accent-*` 를 가리키게 되고, 그건 「값 없음」이라 강조가 통째로 사라진다.
 */
export function initAppearance(): void {
  applyAccent(useAccentStore.getState().accent)
  applyGlass(useGlassStore.getState().glass)
}
